import { createSlice } from "@reduxjs/toolkit";

export interface TenantOption {
  tenantId: number;
  tenantName: string;
  isCurrent: boolean;
}

interface User {
  name: string;
  role?: string;
  email: string;
  profileImage?: string;
  // Tenancy: display-only (header/branding). Never sent back to the server as
  // a filter — the server derives tenant from the validated JWT claim only.
  // See Guides/TENANCY-DESIGN.md §5.1 in the CM-Central backend repo.
  tenantName?: string;
  // Display-only as well (e.g. CM Agri's kanban department picker). 0 for a
  // platform admin, who belongs to no company.
  tenantId?: number;
  // Companies this login belongs to (one login, several companies). The
  // session is in ONE of them at a time; switching goes through
  // Account/SwitchTenant, which re-checks membership and mints a new token.
  tenants?: TenantOption[];
  // Invitations to join another company, waiting for an answer.
  pendingInviteCount?: number;
  // Gates platform-admin-only screens (company create/suspend, sharing
  // config). The server independently re-checks this on every request via
  // [RequiresPlatformAdmin] — this flag only controls whether the FE SHOWS
  // those screens, never whether the backend allows the action.
  isPlatformAdmin?: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}
const loadState = (): AuthState | null => {
  try {
    const serializedState = localStorage.getItem("AuthState");
    if (serializedState === null) return null;
    return JSON.parse(serializedState);
  } catch {
    return null;
  }
};

const initialState: AuthState = loadState() || {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

const saveState = (state: AuthState) => {
  localStorage.setItem("AuthState", JSON.stringify(state));
};
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, { payload }) => {
      state.user = payload;
      state.isAuthenticated = true;
      saveState(state);
    },
    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      localStorage.removeItem("AuthState");
      sessionStorage.removeItem("AuthState");
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
    },
  },
});

export const { setUser, clearUser, setLoading, setError } = authSlice.actions;
export default authSlice.reducer; 